import React, { useState, useEffect } from 'react';
import { Select, Tooltip } from 'antd';
import { adminApi } from '@/services/api';
import type { ConfigVersion } from '@/types';

interface ConfigVersionSelectProps {
  /** Selected config version id, or undefined/null for "shared by every build". */
  value?: string | null;
  onChange?: (value: string | null) => void;
  disabled?: boolean;
  /** Label for the shared option. Override to fit the surrounding content. */
  sharedLabel?: string;
  style?: React.CSSProperties;
  allowClear?: boolean;
}

/**
 * Picks which config version a piece of content belongs to.
 *
 * The empty selection is the important one: it means the row is shared by every
 * build, which is what all existing content is and what anything new should be
 * unless it is deliberately being varied. Choosing a config version narrows the
 * row to the builds mapped to that version.
 */
export const ConfigVersionSelect: React.FC<ConfigVersionSelectProps> = ({
  value,
  onChange,
  disabled,
  sharedLabel = 'All builds (shared)',
  style,
  allowClear = true,
}) => {
  const [versions, setVersions] = useState<ConfigVersion[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await adminApi.getConfigVersions();
        if (!cancelled) setVersions(res.data ?? []);
      } catch {
        // A missing endpoint just means no config versions to choose from;
        // the shared option alone still gives correct behaviour.
        if (!cancelled) setVersions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Tooltip
      title={
        'Leave as shared and every build sees this. Pick a config version and only ' +
        'the app versions mapped to it will — set that up under Config → App Versions.'
      }
    >
      <Select
        value={value ?? undefined}
        onChange={(v) => onChange?.(v ?? null)}
        disabled={disabled}
        loading={loading}
        allowClear={allowClear}
        placeholder={sharedLabel}
        style={{ minWidth: 220, ...style }}
        options={versions.map((cv) => ({
          value: cv.id,
          label: cv.description ? `${cv.version} — ${cv.description}` : cv.version,
        }))}
      />
    </Tooltip>
  );
};

export default ConfigVersionSelect;
